import { Button, ButtonDropdown, ButtonsGroup, Flex, NavItem, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ModalId } from '../../../../../../enums/modalId'
import { getIsSubmitActionTypeCreate } from '../../../../utils/getIsSubmitActionTypeCreate'
import { useCustomerForm } from '../../contexts/customerFormContext'
import { CustomerOptionalItem } from '../../enums/customerOptionalItem'
import { useTrackCustomerModal } from '../../hooks/useTrackCustomerModal'

export const CustomerModalFooter = (): ReactElement => {
  const { t } = useTranslation()
  const { trackCustomerModal } = useTrackCustomerModal()
  const { activeOptionalFormItems, addOptionalFormItem, isLoading, isSubmitting, submitForm, submitActionType } =
    useCustomerForm()

  const { close: closeCustomerModal } = useModal(ModalId.CustomerModal)
  const isSubmitActionTypeCreate = getIsSubmitActionTypeCreate(submitActionType)

  const dropdownItems = useMemo(
    (): NavItem<CustomerOptionalItem>[] => [
      {
        id: CustomerOptionalItem.CustomerNumber,
        children: t('customer_modal.footer.dropdown.add_options.customer_number'),
        iconLeft: 'barcode',
        value: CustomerOptionalItem.CustomerNumber,
      },
      {
        id: CustomerOptionalItem.LanguageAndCurrency,
        children: t('customer_modal.footer.dropdown.add_options.language_and_currency'),
        iconLeft: 'globe',
        value: CustomerOptionalItem.LanguageAndCurrency,
      },
      {
        id: CustomerOptionalItem.PaymentTerm,
        children: t('customer_modal.footer.dropdown.add_options.payment_term'),
        iconLeft: 'calendar',
        value: CustomerOptionalItem.PaymentTerm,
      },
    ],
    [t],
  )

  const activeDropdownItems = useMemo((): NavItem<CustomerOptionalItem>[] => {
    return dropdownItems.filter((item) => !activeOptionalFormItems.includes(item.value))
  }, [activeOptionalFormItems, dropdownItems])

  const handleCustomerAddOptionsItemSelect = useCallback(
    (_: string, selectedOption: CustomerOptionalItem) => {
      trackCustomerModal('Add options selected', { selectedOption })
      addOptionalFormItem(selectedOption)
    },
    [addOptionalFormItem, trackCustomerModal],
  )

  const handleCancelButtonClick = useCallback(() => {
    closeCustomerModal()
  }, [closeCustomerModal])

  const handleSaveButtonClick = useCallback(() => {
    const trackEventDetailMessage = isSubmitActionTypeCreate ? 'Create action clicked' : 'Save changes action clicked'
    trackCustomerModal(trackEventDetailMessage)
    submitForm()
  }, [isSubmitActionTypeCreate, submitForm, trackCustomerModal])

  const buttonLabelTranslationKey = isSubmitActionTypeCreate
    ? 'customer_modal.footer.button.create.label'
    : 'customer_modal.footer.button.save_changes.label'

  return (
    <Flex justifyContent="space-between" width="100%">
      <ButtonDropdown
        disabled={isLoading || isSubmitting || !activeDropdownItems.length}
        dropdownSize="xl"
        items={activeDropdownItems}
        icon="circlePlus"
        onSelect={handleCustomerAddOptionsItemSelect}
        placement="bottom-start"
        variant="outlined"
      >
        {t('customer_modal.footer.dropdown.add_options.label')}
      </ButtonDropdown>
      <ButtonsGroup>
        <Button disabled={isLoading} onClick={handleCancelButtonClick} variant="text">
          {t('cancel')}
        </Button>
        <Button disabled={isLoading || isSubmitting} loading={isSubmitting} onClick={handleSaveButtonClick}>
          {t(buttonLabelTranslationKey)}
        </Button>
      </ButtonsGroup>
    </Flex>
  )
}
